import { z } from 'zod';
import {
  ContradictionDetectedBySchema,
  DeferredItemSchema,
  DocumentCategorySchema,
  EntitySideSchema,
  FactEvidentiaryStatusSchema,
  FactNatureSchema,
  FactPieceRelationSchema,
  FactSummarySchema,
  IsoDateInputSchema,
  PartyDescriptionSchema,
  PartyProceduralRoleSchema,
  PartyTypeSchema,
  QuoteSchema,
  SlugInputSchema,
  StrategicSideSchema,
  TOOL_JUSTIFICATION_MAX_CHARS,
  clippedText,
} from './entities.js';
import { CivilQualitySchema } from './civil-quality.js';
import { PieceNatureSchema } from './piece-nature.js';
import { ProceduralSideSchema } from './procedural-side.js';
import {
  ProceduralActPhaseSchema,
  ProceduralActTypeSchema,
  ProceduralEnqueteKindSchema,
  getProceduralFactPhaseValidationIssues,
} from './procedural-act.js';
import {
  CategorieIntervenantSchema,
  FonctionActeurProcedureSchema,
  ProceduralObservationKindSchema,
  ProceduralStatusConfidenceSchema,
  ProceduralStatusObservationSchema,
  ProceduralStatusSchema,
  RolePartieProcesSchema,
} from './procedural-status.js';
/**
 * Case Wiki — tool write payloads for the Ingest agent.
 *
 * Every payload is validated at the tool-execute boundary (AgenticSession passes
 * Zod-derived schemas to the AI SDK `tool({...})` call, and the resulting TS
 * types are shared with the tool implementations via `z.infer`).
 *
 * See DEBT-029 §3.2 for contract rationale. Char-length bounds match
 * migration 056_case_wiki_v4_schema.sql CHECK constraints (résumé d'un fait : relevé par la
 * migration 199, borne nommée dans `entities.ts`).
 */

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

const TitleSchema = z.string().min(10).max(200);
const ExplanationSchema = z.string().min(30).max(2000);

function withShape<TShape extends z.ZodRawShape, TSchema extends z.ZodTypeAny>(
  baseSchema: z.ZodObject<TShape>,
  schema: TSchema,
): TSchema & { shape: typeof baseSchema.shape } {
  return Object.assign(schema, { shape: baseSchema.shape });
}

// ---------------------------------------------------------------------------
// Facts
// ---------------------------------------------------------------------------

/**
 * Ingest input for `resolve_or_create_fact`. Upsert on (case_id, slug).
 * `primary_source_piece_slug` and `source_document_category` are DB NOT NULL
 * — they bind the fact to the piece that introduced it.
 */
const ResolveOrCreateFactPayloadBaseSchema = z.object({
  slug: SlugInputSchema,
  title: TitleSchema,
  summary: FactSummarySchema,
  nature: FactNatureSchema,
  side: EntitySideSchema,
  primary_source_piece_slug: SlugInputSchema,
  source_document_category: DocumentCategorySchema,
  source_document_subcategory: z.string().min(1).max(120).optional(),
  event_date: IsoDateInputSchema.nullable().optional(),
  /** M068 — renommé depuis `pivot_rationale`. Importance stratégique du fait (50-600 chars). */
  strategic_importance: z.string().min(50).max(600).optional(),
  evidentiary_status: FactEvidentiaryStatusSchema.optional(),
  tags: z.array(z.string().min(1).max(60)).max(20).optional(),
  /**
   * Type d'acte procédural pénal — à poser UNIQUEMENT sur un fait
   * `nature='procedural'` reconnu comme acte du dossier courant. Omettre si le
   * fait n'est pas un acte typé (hors rail Procédure curaté, visible en chrono).
   * Cf. `procedural-act.ts`.
   */
  procedural_act_type: ProceduralActTypeSchema.optional(),
  /**
   * Phase procédurale explicitement établie par la pièce source. Omettre quand
   * l'acte est ambigu et que la source ne tranche pas.
   */
  procedural_phase: ProceduralActPhaseSchema.optional(),
  /**
   * Raffinement de l'enquête uniquement quand la pièce établit qu'on est en
   * flagrance, préliminaire ou sui generis.
   */
  enquete_kind: ProceduralEnqueteKindSchema.optional(),
});
export const ResolveOrCreateFactPayloadSchema = withShape(
  ResolveOrCreateFactPayloadBaseSchema,
  ResolveOrCreateFactPayloadBaseSchema.superRefine((value, ctx) => {
    for (const issue of getProceduralFactPhaseValidationIssues(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [issue.path],
        message: issue.message,
      });
    }
  }),
);
export type ResolveOrCreateFactPayload = z.infer<typeof ResolveOrCreateFactPayloadSchema>;

/**
 * Partial update for a known fact — consommé par le tool agent `update_fact`
 * (writeTools.ts). La surface d'édition humaine REST a été retirée en
 * PR-E2d round 13 (cf. DEBT-300 — manual_overrides JSONB requis avant de
 * ré-introduire l'édition humaine), donc plus de variante "human" séparée.
 *
 * Pas de `.nullable()` ici : un agent ne doit pas pouvoir effacer un champ
 * existant via un re-run (Codex adversarial review PR-E2d finding #2). Le
 * Reviewer enrichit, jamais n'écrase à blanc.
 */
const UpdateFactPayloadBaseSchema = z.object({
  slug: SlugInputSchema,
  title: TitleSchema.optional(),
  summary: FactSummarySchema.optional(),
  nature: FactNatureSchema.optional(),
  side: EntitySideSchema.optional(),
  event_date: IsoDateInputSchema.optional(),
  strategic_importance: z.string().min(50).max(600).optional(),
  evidentiary_status: FactEvidentiaryStatusSchema.optional(),
  source_document_subcategory: z.string().min(1).max(120).optional(),
  tags: z.array(z.string().min(1).max(60)).max(20).optional(),
  /** Type d'acte procédural (cf. ResolveOrCreateFactPayloadSchema). Backstop Review. */
  procedural_act_type: ProceduralActTypeSchema.optional(),
  /** Phase procédurale explicitement sourcée. */
  procedural_phase: ProceduralActPhaseSchema.optional(),
  /**
   * Sous-type d'enquête. La couche repository peut l'accepter seule si la
   * ligne existante porte déjà `procedural_phase='enquete'`.
   */
  enquete_kind: ProceduralEnqueteKindSchema.optional(),
});
export const UpdateFactPayloadSchema = withShape(
  UpdateFactPayloadBaseSchema,
  UpdateFactPayloadBaseSchema.superRefine((value, ctx) => {
    for (const issue of getProceduralFactPhaseValidationIssues(value, {
      allow_implicit_existing_enquete_phase_for_kind: true,
    })) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [issue.path],
        message: issue.message,
      });
    }
  }),
);
export type UpdateFactPayload = z.infer<typeof UpdateFactPayloadSchema>;

// ---------------------------------------------------------------------------
// Parties
// ---------------------------------------------------------------------------

/**
 * Ingest input for `resolve_or_create_party`.
 *
 * `strategic_side` and `civil_quality` are arbitrated by the agent using the
 * client_name + procedure_type passed in the user prompt — the lawyer never
 * has to mark parties manually. Cf. system-prompt-ingest.md "Identification
 * stratégique des parties".
 */
export const ResolveOrCreatePartyPayloadSchema = z.object({
  slug: SlugInputSchema,
  title: z.string().min(2).max(200),
  aliases: z.array(z.string().min(1).max(200)).max(20).optional(),
  party_type: PartyTypeSchema,
  side: EntitySideSchema,
  short_description: PartyDescriptionSchema.optional(),
  procedural_roles: z.array(PartyProceduralRoleSchema).max(20).optional(),
  /**
   * Rôle stratégique au procès — orthogonal à `side` (provenance documentaire).
   * Défaut `'unset'` si l'agent n'arrive pas à arbitrer.
   */
  strategic_side: StrategicSideSchema.optional(),
  /**
   * Qualité civile/procédurale, choisie dans le catalogue contextuel à
   * `procedure_type` (cf. `civil-quality.ts`). `'autre'` plutôt qu'inventer.
   */
  civil_quality: CivilQualitySchema.optional(),
  /** PR3c — groupe d'affichage pénal V1 (vraie partie, acteur, représentant, cité). */
  categorie_intervenant: CategorieIntervenantSchema.optional(),
  /** PR3c — rôle d'une vraie partie au procès (MP, prévenu, accusé...). */
  role_partie_proces: RolePartieProcesSchema.optional(),
  /** PR3c — fonction d'un acteur procédural nommé (OPJ, substitut, JLD...). */
  fonction_acteur_procedure: FonctionActeurProcedureSchema.optional(),
});
export type ResolveOrCreatePartyPayload = z.infer<typeof ResolveOrCreatePartyPayloadSchema>;

/**
 * Partial update for a known party — consommé par le tool agent
 * `update_party` (writeTools.ts). Surface d'édition humaine retirée en
 * PR-E2d round 13 (cf. DEBT-300). Pas de `.nullable()` : un agent ne doit
 * pas pouvoir effacer un champ via un re-run, y compris les nouveaux champs
 * de classification pénale V1.
 */
export const UpdatePartyPayloadSchema = z.object({
  slug: SlugInputSchema,
  title: z.string().min(2).max(200).optional(),
  aliases: z.array(z.string().min(1).max(200)).max(20).optional(),
  party_type: PartyTypeSchema.optional(),
  side: EntitySideSchema.optional(),
  short_description: PartyDescriptionSchema.optional(),
  procedural_roles: z.array(PartyProceduralRoleSchema).max(20).optional(),
  strategic_side: StrategicSideSchema.optional(),
  civil_quality: CivilQualitySchema.optional(),
  categorie_intervenant: CategorieIntervenantSchema.optional(),
  role_partie_proces: RolePartieProcesSchema.optional(),
  fonction_acteur_procedure: FonctionActeurProcedureSchema.optional(),
});
export type UpdatePartyPayload = z.infer<typeof UpdatePartyPayloadSchema>;

/**
 * Consolidation-only cleanup for stale penal participant classification.
 *
 * Unlike generic `update_party`, this bounded payload can explicitly clear the
 * two nullable penal role fields. The booleans make destructive cleanup
 * auditable in `case_wiki_runs.payload.steps`; omitted fields never clear data.
 */
export const CleanPenalPartyClassificationPayloadSchema = z
  .object({
    party_slug: SlugInputSchema,
    categorie_intervenant: CategorieIntervenantSchema.optional(),
    strategic_side: StrategicSideSchema.optional(),
    civil_quality: CivilQualitySchema.optional(),
    short_description: PartyDescriptionSchema.optional(),
    role_partie_proces: RolePartieProcesSchema.optional(),
    fonction_acteur_procedure: FonctionActeurProcedureSchema.optional(),
    clear_role_partie_proces: z.boolean().optional(),
    clear_fonction_acteur_procedure: z.boolean().optional(),
    source_fact_slug: SlugInputSchema.optional(),
    source_piece_slug: SlugInputSchema.optional(),
    source_quote: QuoteSchema.optional(),
    reason: z.string().min(10).max(500),
  })
  .superRefine((value, ctx) => {
    if (
      value.role_partie_proces !== undefined &&
      value.clear_role_partie_proces === true
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['clear_role_partie_proces'],
        message:
          'clear_role_partie_proces cannot be true when role_partie_proces is also provided',
      });
    }
    if (
      value.fonction_acteur_procedure !== undefined &&
      value.clear_fonction_acteur_procedure === true
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['clear_fonction_acteur_procedure'],
        message:
          'clear_fonction_acteur_procedure cannot be true when fonction_acteur_procedure is also provided',
      });
    }

    const hasUpdate =
      value.categorie_intervenant !== undefined ||
      value.strategic_side !== undefined ||
      value.civil_quality !== undefined ||
      value.short_description !== undefined ||
      value.role_partie_proces !== undefined ||
      value.fonction_acteur_procedure !== undefined ||
      value.clear_role_partie_proces === true ||
      value.clear_fonction_acteur_procedure === true;

    if (!hasUpdate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['party_slug'],
        message:
          'clean_penal_party_classification requires at least one classification field or explicit clear flag',
      });
    }

    if (
      value.source_fact_slug === undefined &&
      value.source_piece_slug === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['source_fact_slug'],
        message:
          'source_fact_slug or source_piece_slug is required to keep the cleanup source-bound',
      });
    }
  });
export type CleanPenalPartyClassificationPayload = z.infer<
  typeof CleanPenalPartyClassificationPayloadSchema
>;

/**
 * Fusion de deux parties pendant la consolidation `clean_participants`.
 *
 * Plus strict que le `MergePartiesPayloadSchema` du lint (qui ne porte qu'un
 * `rationale`) : la consolidation NETTOIE le modèle, donc chaque fusion est un
 * acte tracé qui doit être SOURCE-BOUND (au moins `source_fact_slug` ou
 * `source_piece_slug`), comme `clean_penal_party_classification`. Le repository
 * (`mergeParties`) ré-ancre `case_party_procedural_statuses` + `case_fact_parties`
 * vers le winner AVANT le DELETE du loser (cascade-safe) — voir l'invariant
 * pénal append-only (services/case-wiki/CLAUDE.md).
 */
export const MergePartiesConsolidationPayloadSchema = z
  .object({
    winner_slug: SlugInputSchema,
    loser_slug: SlugInputSchema,
    // Justification renvoyée telle quelle dans le résultat de l'outil, jamais écrite en base :
    // coupée au-delà du maximum, pas refusée.
    reason: clippedText(10, TOOL_JUSTIFICATION_MAX_CHARS),
    source_fact_slug: SlugInputSchema.optional(),
    source_piece_slug: SlugInputSchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (value.winner_slug === value.loser_slug) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['loser_slug'],
        message: 'winner_slug and loser_slug must differ',
      });
    }
    if (!value.source_fact_slug && !value.source_piece_slug) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['source_fact_slug'],
        message:
          'merge_parties (consolidation) is source-bound: provide source_fact_slug or source_piece_slug',
      });
    }
  });
export type MergePartiesConsolidationPayload = z.infer<
  typeof MergePartiesConsolidationPayloadSchema
>;

/**
 * Ingest-only writer for source-bound penal procedural status observations.
 * Status observations remain slug-based on the wire even though the DB stores
 * UUID FKs; at least one source lets the repository enforce same-case fact or
 * piece provenance.
 */
export const RecordPartyProceduralStatusPayloadSchema = z
  .object({
    party_slug: SlugInputSchema,
    observation_kind: ProceduralObservationKindSchema,
    status: ProceduralStatusSchema,
    phase: ProceduralActPhaseSchema.nullable().optional(),
    effective_date: IsoDateInputSchema.nullable().optional(),
    source_fact_slug: SlugInputSchema.optional(),
    source_piece_slug: SlugInputSchema.optional(),
    source_quote: QuoteSchema.optional(),
    confidence: ProceduralStatusConfidenceSchema.optional(),
  })
  .superRefine((value, ctx) => {
    const pairResult = ProceduralStatusObservationSchema.safeParse({
      observation_kind: value.observation_kind,
      status: value.status,
    });
    if (!pairResult.success) {
      for (const issue of pairResult.error.issues) {
        ctx.addIssue(issue);
      }
    }

    if (
      value.source_fact_slug === undefined &&
      value.source_piece_slug === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['source_fact_slug'],
        message:
          'source_fact_slug or source_piece_slug is required to keep the observation source-bound',
      });
    }
  });
export type RecordPartyProceduralStatusPayload = z.infer<
  typeof RecordPartyProceduralStatusPayloadSchema
>;

// Le sous-graphe "références juridiques" (`resolve_or_create_article` /
// `resolve_or_create_jurisprudence` + payloads `link_piece_to_*`) a été retiré
// le 2026-07-03 (décision handoff case-wiki-virtual-fs §3.7) : les tables
// case_legal_articles / case_jurisprudence n'étaient exploitées par aucune
// surface produit.

// ---------------------------------------------------------------------------
// Relations (link_*)
// ---------------------------------------------------------------------------

/** Link a party to a fact with an optional per-fact role. */
export const LinkFactPartyPayloadSchema = z.object({
  fact_slug: SlugInputSchema,
  party_slug: SlugInputSchema,
  role: z.string().min(2).max(120).optional(),
});
export type LinkFactPartyPayload = z.infer<typeof LinkFactPartyPayloadSchema>;

/**
 * Link a piece to a fact. `quote` (10-2000) mandatory when relation is
 * `source` or `contradicts` — DB CHECK enforces it, the superRefine mirrors it
 * at the tool boundary so the agent gets an actionable error before the DB.
 */
export const LinkFactPiecePayloadSchema = z
  .object({
    fact_slug: SlugInputSchema,
    piece_slug: SlugInputSchema,
    relation: FactPieceRelationSchema,
    quote: QuoteSchema.optional(),
    quote_location: z.string().min(1).max(200).optional(),
  })
  .superRefine((value, ctx) => {
    if (
      (value.relation === 'source' || value.relation === 'contradicts') &&
      (value.quote === undefined || value.quote.trim() === '')
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['quote'],
        message: `quote is required when relation is "${value.relation}"`,
      });
    }
  });
export type LinkFactPiecePayload = z.infer<typeof LinkFactPiecePayloadSchema>;

/**
 * Partie rattachée à un fait dès sa création (`resolve_or_create_fact.parties[]`) :
 * un `link_fact_party` dont le fait est celui qu'on crée.
 */
export const FactPartyLinkSchema = LinkFactPartyPayloadSchema.omit({ fact_slug: true });
export type FactPartyLink = z.infer<typeof FactPartyLinkSchema>;

/**
 * Citation qui rattache un fait à la pièce en cours d'ingestion
 * (`resolve_or_create_fact.source`) : un `link_fact_piece(relation='source')`
 * dont le fait et la pièce sont connus du serveur.
 */
export const FactSourceQuoteSchema = z.object({
  quote: QuoteSchema,
  quote_location: z.string().min(1).max(200).optional(),
});
export type FactSourceQuote = z.infer<typeof FactSourceQuoteSchema>;

/**
 * Création d'un fait avec ses liens, écrite en une transaction par
 * `CaseWikiRepository.resolveOrCreateFactWithLinks` (issue #2048).
 */
export type ResolveOrCreateFactWithLinksPayload = ResolveOrCreateFactPayload & {
  clear_procedural_phase?: boolean;
  clear_enquete_kind?: boolean;
  parties: FactPartyLink[];
  source: FactSourceQuote;
};

// ---------------------------------------------------------------------------
// Contradictions & Questions
// ---------------------------------------------------------------------------

/**
 * Append a contradiction. `title` mandatory (10-200). At least one of
 * `fact_b_slug` / `piece_b_slug` required (DB CHECK + superRefine).
 */
export const AppendContradictionPayloadSchema = z
  .object({
    title: TitleSchema,
    fact_a_slug: SlugInputSchema,
    fact_b_slug: SlugInputSchema.optional(),
    piece_b_slug: SlugInputSchema.optional(),
    explanation: ExplanationSchema,
    quote_a: QuoteSchema.optional(),
    quote_b: QuoteSchema.optional(),
    detected_by: ContradictionDetectedBySchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (!value.fact_b_slug && !value.piece_b_slug) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fact_b_slug'],
        message: 'at least one of fact_b_slug or piece_b_slug is required',
      });
    }
  });
export type AppendContradictionPayload = z.infer<typeof AppendContradictionPayloadSchema>;

// `AppendQuestionPayloadSchema` retiré le 2026-05-29 (refonte L-W-B).
// La table `case_questions` est DROP par la migration 097.
// Le besoin est couvert par le `litigation_review` structuré du Reviewer V2
// (disputed_nodes / evidence_to_obtain / client_clarifications).

// ---------------------------------------------------------------------------
// Finish tool
// ---------------------------------------------------------------------------

/**
 * Terminal tool call for an ingest session. The runner stores this in
 * `case_wiki_runs.payload` along with the usage counters.
 */
export const FinishIngestPayloadSchema = z.object({
  summary: z.string().min(20).max(2000),
  facts_created_slugs: z.array(SlugInputSchema).max(200).default([]),
  facts_updated_slugs: z.array(SlugInputSchema).max(200).default([]),
  parties_touched_slugs: z.array(SlugInputSchema).max(100).default([]),
  // `articles_referenced_slugs` / `jurisprudence_referenced_slugs` retirés le
  // 2026-07-03 avec le sous-graphe références juridiques. Les anciens runs
  // persistés qui portent encore ces clés restent tolérés par Zod (clés
  // inconnues strippées au parse).
  /**
   * Number of contradictions appended via `append_contradiction` during
   * this run. Kept as a count because `case_contradictions` rows are
   * UUID-only (no slug column). The server counts the accepted calls; the
   * actual rows can be cross-referenced via
   * `case_contradictions WHERE case_id = ?`.
   */
  contradictions_flagged: z.number().int().nonnegative().default(0),
  // `questions_raised` retiré 2026-05-29 (refonte L-W-B + migration 097).
  // Les anciens runs persistés en `case_wiki_runs.payload` qui contiennent
  // encore ce champ sont tolérés par défaut Zod (`.passthrough()` au niveau
  // du `case_wiki_runs.payload` consumer si besoin) — le retirer du contrat
  // arrête simplement l'agent d'avoir à le produire.
  piece_quality: z.enum(['complete', 'partial', 'redundant']),
  /**
   * Nature juridique de la pièce ingérée (axe orthogonal à
   * `document_category`). L'agent retourne une valeur du closed enum
   * `PieceNatureSchema`, ou `autre` plutôt qu'inventer en cas de doute.
   * Optionnel pour ne pas casser les anciens runs persistés en `payload`.
   * Cf. `piece-nature.ts` pour la taxonomie complète et les descriptions.
   */
  piece_nature: PieceNatureSchema.optional(),
  /**
   * Camp procédural de la pièce — qui l'a produite dans la procédure.
   * Indispensable pour la timeline des écritures (page Écritures du
   * dashboard Case Wiki, cf migration M069 + DEBT-131).
   *
   *   - our_party       : produite par notre client / nos avocats
   *   - opposing_party  : produite par l'adversaire
   *   - court           : produite par le juge (jugement, ordonnance, arrêt)
   *   - neutral         : hors procédure ou camp non identifiable
   *
   * Optionnel pour ne pas casser les runs antérieurs à M069. L'agent doit
   * remplir cette valeur pour toute pièce de nature procédurale ; pour les
   * pièces non procédurales, retourner `neutral`.
   */
  procedural_side: ProceduralSideSchema.optional(),
  /** Items différés par l'agent ingest — carry-forward vers la phase lint/review. */
  deferred_items: z.array(DeferredItemSchema).max(200).default([]),
});
export type FinishIngestPayload = z.infer<typeof FinishIngestPayloadSchema>;

/**
 * Ce que l'agent déclare en appelant `finish_ingest`. Les faits créés, les faits
 * et parties touchés et le nombre de contradictions n'y figurent pas : le serveur
 * les relève sur les appels d'outil acceptés de la session et les ajoute au
 * `FinishIngestPayload` enregistré (issue #2048).
 */
export const FinishIngestInputSchema = FinishIngestPayloadSchema.omit({
  facts_created_slugs: true,
  facts_updated_slugs: true,
  parties_touched_slugs: true,
  contradictions_flagged: true,
});
export type FinishIngestInput = z.infer<typeof FinishIngestInputSchema>;
